import _ from "lodash";
/**--external-- */
import React from "react";
import update from "immutability-helper";
import { connect } from "react-redux";
import classnames from "classnames";

/**-internal-- */
import { compose } from "lib/apolloClient";
import I18nHOC from "UIComponents/I18nHOC";
import UIBaseComponent from "UIComponents/UIBaseComponent";

/**--relative-- */
import GenericCheckBox from "./components/GenericCheckBox";
import classes from "./GenericCheckList.scss";
import GenericCheckBoxView from "./components/GenericCheckBoxView";
import { generateSelectedNodesWithParentsSet } from "Utils/BenchmarkDataHelper";

/*
  showTextBoxOnSelect:
    used to show textbox when checkbox is selected
  showSubtextUnderInfo:
    used to show subtext under info icon
  isGroupedOptions:
    used to group the options
  options:
    option list which is used to render the checkbox
  value:
    selected options
  isMultiColumn:
    used to display checkboxes in 2 columns instead of 1
*/

class GenericCheckList extends UIBaseComponent {
  constructor(props) {
    super(props);
  }

  UNSAFE_componentWillReceiveProps = nextProps => {
    // used to reset the value when options are changed dynamically
    if (_.xorBy(this.props.options, nextProps.options, "value").length > 0) {
      const updatedValue = _.filter(
        nextProps.value,
        val => _.findIndex(nextProps.options, { value: val }) != -1
      );

      this.updateValue(updatedValue, { nodes: this.props.options });
    }
  };

  onCheckBoxChange = ({ checkboxId, actionType, updatedText, textboxUid }) => {
    const {
      value,
      showTextBoxOnSelect,
      subFieldValue,
      updateInputField,
      subField,
      parentNodes,
      options,
      shouldFilterParentNodesOnUpdate,
    } = this.props;

    let updatedValue = value || [];

    switch (actionType) {
      case "SELECT": {
        updatedValue = update(updatedValue, { $push: [checkboxId] });
        break;
      }
      case "DESELECT": {
        const index = _.indexOf(updatedValue, checkboxId);
        if (index !== -1) {
          updatedValue = update(updatedValue, { $splice: [[index, 1]] });
        }
        break;
      }
      default:
        break;
    }
    if (!_.isEqual(actionType, "UPDATE_TEXT")) {
      const parentNodeIds = parentNodes.map(node => node.id);
      // shouldFilterParentNodesOnUpdate case specifically written for key concepts of MYP where we create parent nodes manually
      // so we need to filter parent nodes on update as well to prevent it from updating in resolvedMinimalTree
      const updatedOptions = shouldFilterParentNodesOnUpdate
        ? _.filter(options, option => !_.includes(parentNodeIds, option.id))
        : options;

      this.updateValue(updatedValue, {
        nodes: updatedOptions,
      });
    }
    if (showTextBoxOnSelect) {
      let updatedSubFieldValue = subFieldValue || {};
      switch (actionType) {
        case "SELECT": {
          updatedSubFieldValue = {
            ...subFieldValue,
            [checkboxId]: {
              ..._.get(subFieldValue, checkboxId, {}),
              [textboxUid]: _.get(
                subFieldValue,
                `[${checkboxId}].[${textboxUid}]`
              ),
            },
          };
          break;
        }
        case "UPDATE_TEXT": {
          updatedSubFieldValue = {
            ...subFieldValue,
            [checkboxId]: {
              ..._.get(subFieldValue, checkboxId, {}),
              [textboxUid]: updatedText,
            },
          };
          break;
        }
        default:
          break;
      }
      const subFieldKey = _.get(subField, "uid");
      updateInputField(
        {
          [subFieldKey]: updatedSubFieldValue,
        },
        {},
        true,
        false
      );
    }
  };

  renderViewEmpty = () => {
    const { t, emptyText } = this.props;
    return (
      <span className={classnames("text-body", classes.emptyTextContainer)}>
        {emptyText ? emptyText : t("common:not_selected")}
      </span>
    );
  };

  shouldShowViewEmpty = () => {
    return _.isEmpty(this.props.value);
  };

  renderFlatCheckList = ({ checkListOptions, isRootItem }) => {
    const {
      showTextBoxOnSelect,
      subFieldValue,
      showSubtextUnderInfo,
      tooltipPlacement,
      fixArrowPosition,
      valuesSet,
      isMultiColumn,
      disabled,
      testId,
    } = this.props;

    return _.isEmpty(checkListOptions) ? (
      this.renderViewEmpty()
    ) : (
      <div
        className={classnames(classes.checkListContainer, {
          [classes.multiColumnLayout]: isMultiColumn,
        })}
      >
        {_.map(checkListOptions, item => {
          const isSelected = valuesSet.has(item.id);

          const textboxUid = _.get(item, "textboxUid", null);

          const textboxValue = _.get(
            subFieldValue,
            `[${item.id}].[${textboxUid}]`
          );

          return (
            <GenericCheckBox
              key={item.id}
              item={item}
              onChange={this.onCheckBoxChange}
              showTextBoxOnSelect={showTextBoxOnSelect}
              isSelected={isSelected}
              textboxValue={textboxValue}
              showSubtextUnderInfo={showSubtextUnderInfo}
              tooltipPlacement={tooltipPlacement}
              fixArrowPosition={fixArrowPosition}
              isRootItem={isRootItem}
              disabled={disabled}
              testId={`${testId}-${item.id}`}
            />
          );
        })}
      </div>
    );
  };

  renderEdit = () => {
    const { isGrouped, idNodeMap, parentNodes } = this.props;
    if (!isGrouped) {
      return this.renderFlatCheckList({ checkListOptions: parentNodes });
    } else {
      return (
        <div className={classes.groupsContainer}>
          {_.map(parentNodes, parentItem => {
            const { label, children, id } = parentItem;

            const childrenNodes = _.reduce(
              children,
              (acc, childId) => {
                const node = idNodeMap[childId];
                if (node) acc.push(node);
                return acc;
              },
              []
            );
            const isLeafNode = _.isEmpty(childrenNodes);

            return (
              <div className={classes.groupContainer} key={id}>
                {/* Render group label if children exist */}
                {!isLeafNode && (
                  <span
                    className={classnames("text-label", classes.groupLabel)}
                  >
                    {label}
                  </span>
                )}
                {/* Render checklist for children or parent as root item */}
                {this.renderFlatCheckList({
                  checkListOptions: !isLeafNode ? childrenNodes : [parentItem],
                  isRootItem: isLeafNode,
                })}
              </div>
            );
          })}
        </div>
      );
    }
  };

  renderViewFlatCheckList = ({ checkListOptions }) => {
    const { subFieldValue, isMultiColumn } = this.props;
    return _.isEmpty(checkListOptions) ? (
      this.renderViewEmpty()
    ) : (
      <div
        className={classnames(classes.viewContainer, {
          [classes.multiColumnLayout]: isMultiColumn,
        })}
      >
        {_.map(checkListOptions, item => {
          const textboxUid = _.get(item, "textboxUid", null);

          const textboxValue = _.get(
            subFieldValue,
            `[${item.id}].[${textboxUid}]`
          );
          return (
            <GenericCheckBoxView
              key={item.id}
              item={item}
              textboxValue={textboxValue}
            />
          );
        })}
      </div>
    );
  };

  renderView = () => {
    const { options, value, isGrouped, idNodeMap, parentNodes } = this.props;

    const selectedNodesSet = generateSelectedNodesWithParentsSet({
      nodes: options,
      value,
    });

    // only show the parent if there is subnodes(grouping case) || parent itself (non-grouping case) is selected
    const filteredParentNodes = _.filter(parentNodes, node =>
      selectedNodesSet.has(node.id)
    );

    return isGrouped ? (
      <div className={classes.groupsContainer}>
        {_.map(filteredParentNodes, parentItem => {
          const { label, children, id } = parentItem;
          // filter out the selected children and fetch their node
          const childrenNodes = _.map(
            _.filter(children, childId => selectedNodesSet.has(childId)),
            childId => idNodeMap[childId]
          );
          return (
            <div className={classes.viewGroupContainer} key={id}>
              <span className={classnames("text-label", classes.groupLabel)}>
                {label}
              </span>
              {!_.isEmpty(childrenNodes) &&
                this.renderViewFlatCheckList({
                  checkListOptions: childrenNodes,
                })}
            </div>
          );
        })}
      </div>
    ) : (
      this.renderViewFlatCheckList({ checkListOptions: filteredParentNodes })
    );
  };
}

const mapStateToProps = (state, ownProps) => {
  const { value, options } = ownProps;
  // create a map of id to node for faster lookup
  const idNodeMap = _.keyBy(options, "id");
  // create a set of selected values for faster lookup
  const valuesSet = new Set(value);

  // filter out the nodes whose parent is null
  const parentNodes = _.filter(options, option => !option.parent);
  // check if the options are grouped, if there is only one level then sizes will be equal
  const isGrouped = !_.isEqual(_.size(parentNodes), _.size(options));
  return {
    isGrouped,
    idNodeMap,
    valuesSet,
    parentNodes,
  };
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, null)
)(GenericCheckList);

GenericCheckList.defaultProps = {
  ...UIBaseComponent.defaultProps,
  showTextBoxOnSelect: false,
  showSubtextUnderInfo: false,
  isGroupedOptions: false,
  options: [],
  value: [],
  shouldFilterParentNodesOnUpdate: false,
  isMultiColumn: false,
};

GenericCheckList.displayName = "GenericCheckList";
